<template>
  <div>
    <adjustment-tabs :type="type"></adjustment-tabs>
    <m-form
      :title="title"
      :showList="false"
      :listRoute="{ name: 'AdjustmentsList', params: { type: type } }"
      exportBaseUrl="reports/lists/adjustments"
      :exportParams="{ id: id }"
      :isEditing.sync="isEditing"
      :canDelete="canDelete"
      :canSubmit="canSubmit"
      :canEdit="canEdit"
      @clearForm="clearForm(true)"
      @goToForm="getItemByPosition"
      @submit="validate"
      @delete="deleteItem"
      @define="defineItem"
    >
      <template #header-btns>
        <open-sanad-btn v-if="item.sanad" :sanad="item.sanad" />

        <v-btn
          v-if="item.warehouseHandling"
          :to="{ name: 'WarehouseHandlingForm', params: { id: item.warehouseHandling.id } }"
          class="mt-1 mr-1 ml-1 elevation-0 cursor-pointer rounded-lg blue white--text"
          :disabled="disabled"
        >
          <!-- <v-icon class="cursor-pointer ml-2">$sanadIcon</v-icon> -->
          انبارگردانی
          {{ item.warehouseHandling.code }}
        </v-btn>
      </template>
      <template>
        <v-row>
          <v-col cols="12">
            <v-row>
              <v-col cols="12" md="2">
                <div id="adjustment-form-code">
                  <v-text-field class="rounded-lg" label="شماره" disabled v-model="item.code" />
                </div>
              </v-col>
              <v-col cols="12" md="2">
                <div id="adjustment-form-date">
                  <date label=" * تاریخ" v-model="item.date" :default="true" :disabled="!isEditing" />
                </div>
              </v-col>
              <v-col cols="12" md="2">
                <div id="adjustment-form-time">
                  <mtime label=" * ساعت" required v-model="item.time" :default="true" :disabled="!isEditing" />
                </div>
              </v-col>
              <v-col cols="12" md="2">
                <div id="adjustment-form-is-defined">
                  <v-switch inset v-if="item.id" label="قطعی شده" v-model="item.is_defined" disabled />
                </div>
              </v-col>
              <v-col cols="4">
                <div id="adjustment-form-explanation">
                  <v-textarea
                    label="توضیحات"
                    class="form-control rounded-lg"
                    v-model="item.explanation"
                    :disabled="!isEditing"
                  />
                </div>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <input-table v-model="rows" :fixed_header="true" :height="inputTableHeight">
              <template #thead>
                <tr>
                  <th class="secondary--text tableHeader">#</th>
                  <th class="secondary--text tableHeader">* نام/کد کالا</th>
                  <th class="secondary--text tableHeader">* واحد</th>
                  <th class="secondary--text tableHeader">* انبار</th>
                  <th class="secondary--text tableHeader">* تعداد</th>
                  <th class="secondary--text tableHeader">قیمت واحد</th>
                  <th class="secondary--text tableHeader">مبلغ</th>
                  <th class="secondary--text tableHeader">توضیحات</th>
                  <th class="d-print-none tableHeader"><v-icon color="secondary" x-small>fa-trash</v-icon></th>
                </tr>
              </template>
              <template #tbody>
                <tr v-for="(row, i) in rows" :key="i"
                    :class="{ 'd-print-none': i == rows.length - 1, draggable: i != rows.length - 1 && isEditing }"
                >
                  <td class="tr-counter">{{ i + 1 }}</td>
                  <td class="tr-ware" id="adjustment-row-ware">
                    <ware-select
                      @input="d.getLastBuyPrice(i)"
                      v-model="rows[i].ware"
                      :disabled="!isEditing"
                      :show-main-unit="false"
                    />
                  </td>
                  <td style="max-width: 100px"  id="adjustment-row-unit">
                    <v-autocomplete
                      class="rounded-lg"
                      v-if="rows[i].ware"
                      :items="getWareUnits(row.ware)"
                      v-model="rows[i].unit"
                      :title="rows[i].unit ? rows[i].unit.title : ''"
                      item-text="name"
                      item-value="id"
                      :disabled="!isEditing"
                      :return-object="true"
                      :suffix="getUnitSuffix(row.ware, row.unit)"
                    ></v-autocomplete>
                  </td>

                  <td class="tr-warehouse" id="adjustment-row-warehouse">
                    <v-autocomplete
                      class="rounded-lg"
                      :return-object="true"
                      v-if="rows[i].ware"
                      :items="warehouses"
                      v-model="rows[i].warehouse"
                      item-text="name"
                      item-value="id"
                      :disabled="!isEditing"
                    />
                    <span v-else>-</span>
                  </td>
                  <td id="adjustment-row-count">
                    <money v-model="rows[i].unit_count" :disabled="!isEditing" />
                  </td>
                  <td id="adjustment-row-fee">
                    <money v-model="rows[i].fee" :disabled="!isEditing || rows[i].serverFee" />
                  </td>
                  <td id="adjustment-row-amount">
                    <money :value="rows[i].fee * rows[i].unit_count" :disabled="true" />
                  </td>
                  <td id="adjustment-row-explanation">
                    <row-textarea
                      class="rounded-lg"
                      v-model="rows[i].explanation"
                      :disabled="!isEditing"
                      :i="i"
                      @updateRowsExplanation="updateRowsExplanation"
                    />
                  </td>
                  <td class="d-print-none" id="adjustment-row-delete">
                    <v-btn
                      @click="deleteRow(i)"
                      class="red--text"
                      icon
                      :disabled="!isEditing || (i == rows.length - 1)"
                    >
                      <v-icon>delete</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </template>
              <template #footer>
                <tr class="bg-info white--text secondary">
                  <td colspan="1">
                    <v-btn
                      id="adjustment-delete-all-row"
                      class="rounded-lg error white--text pt-4 pb-4"
                      depressed
                      :disabled="!isEditing"
                      @click="deleteRow(-1)"
                    >
                      حذف همه ردیف ها
                    </v-btn>
                  </td>
                  <td colspan="2"></td>
                  <td class="primary--text">جمع</td>
                  <td>{{ sum | toMoney }}</td>
                  <td></td>
                  <td>{{ valueSum | toMoney }}</td>
                  <td colspan="3"></td>
                </tr>
              </template>
            </input-table>
          </v-col>
        </v-row>
      </template>
    </m-form>
  </div>
</template>

<script>
import wareApiMixin from "@/mixin/wareApi";
import formsMixin from "@/mixin/forms";
import money from "@/components/mcomponents/cleave/Money";
import date from "@/components/mcomponents/cleave/Date";
import mtime from "@/components/mcomponents/cleave/Time";
import _, { takeRightWhile } from "lodash";
import { MFormMixin } from "@/components/m-form";
import AdjustmentTabs from "@/views/panel/adjustment/AdjustmentTabs";
import { mapState } from "vuex";

export default {
  name: "Form",
  components: { AdjustmentTabs, money, date, mtime },
  mixins: [formsMixin, wareApiMixin, MFormMixin],
  props: {
    id: {},
    type: {
      required: true,
    },
  },
  data() {
    return {
      baseUrl: "factors/adjustments",
      appendSlash: true,
      rowKey: "ware",
      hasList: false,
      hasIdProp: true,
      isDefinable: true,
      hasLock: true,
      inventory: {
        ware: null,
        warehouses: [],
      },
      d: {},
    };
  },
  created() {
    this.d.getLastBuyPrice = _.debounce(this.getLastBuyPrice, 10);
  },
  methods: {
    getItemByPosition(position) {
      return this.request({
        url: this.endpoint(`factors/factors/byPosition`),
        method: "get",
        params: {
          id: this.item.id,
          position: position,
          type: this.type,
          is_pre_factor: false,
        },
        success: (data) => {
          this.setItem(data);
        },
      });
    },
    getData() {
      this.getWarehouses();
      this.getUnits();
    },
    validate(clearForm = false) {
      let isValid = true;
      if (this.rows.length == 1) {
        this.notify(`حداقل یک ردیف وارد کنید`, "danger");
        isValid = false;
      }
      this.rows.forEach((r, i) => {
        if (i == this.rows.length - 1) return;
        if (!r.unit_count || r.unit_count == 0) {
          this.notify(`لطفا تعداد ردیف ${i + 1} را وارد کنید`, "danger");
          isValid = false;
        }
      });
      if (!isValid) return;
      this.submit(clearForm);
    },
    getSerialized() {
      let item = this.copy(this.item);
      item = this.extractIds(item);
      item.type = this.type;

      let items = [];
      this.rows.forEach((row, i) => {
        if (i == this.rows.length - 1) return;
        let item = this.copy(row);
        item.count = this.convertToMainUnit(item.ware, item.unit_count, item.unit);
        item = this.extractIds(item);
        items.push(item);
      });

      return {
        item: item,
        items: {
          items: items,
          ids_to_delete: this.itemsToDelete,
        },
      };
    },
    setItem(adjustment) {
      this.item = adjustment;
      this.rows = adjustment.items.sort((a, b) => a.order - b.order);
      this.rows.push(this.copy(this.rowTemplate));

      this.changeRouteTo(adjustment.id);
    },
    getLastBuyPrice(i) {
      if (i >= this.rows.length || this.rows.length == 1) {
        return;
      }
      const ware = this.rows[i].ware;
      this.request({
        url: this.endpoint(`wares/lastBuyPrice`),
        method: "get",
        params: {
          ware: ware.id,
        },
        success: (data) => {
          console.log(data);
          if (data.price) {
            this.$set(this.rows[i], "fee", data.price);
            this.$set(this.rows[i], "serverFee", true);
          }
        },
      });
    },
  },
  computed: {
    ...mapState({
      errorDialog: (state) => state.errorDialog,
    }),
    defineUrl() {
      return "factors/factors/define/";
    },
    toggleLockUrl() {
      return "factors/factors/toggleLock/";
    },
    permissionBasename() {
      if (this.type == "ia") return "inputAdjustment";
      else return "outputAdjustment";
    },
    inputTableHeight() {
      let height = screen.height - 450;
      return height + "px";
    },

    title() {
      let title = "";
      if (this.type == "ia") title = "رسید تعدیل";
      else {
        title = "حواله تعدیل";
      }

      if (this.item.warehouseHandling) {
        title += " انبارگردانی";
      } else {
        title += " انبار";
      }

      return this.setDocumentTitle(title);
    },
    sum() {
      let res = 0;
      this.rows.forEach((row) => {
        if (row.unit_count) res += +row.unit_count;
      });
      return res;
    },
    valueSum() {
      let res = 0;
      this.rows.forEach((row) => {
        if (row.unit_count) res += +row.fee || 0 * +row.unit_count;
      });
      return res;
    },
  },
};
</script>

<style scoped lang="scss"></style>
